import { composeThemeFromProps, Compose } from '@css-modules-theme/react';
import clsx from 'clsx';
import { forwardRef, useImperativeHandle } from 'react';
import { X } from 'react-feather';
import FocusLock from 'react-focus-lock';
import { Transition, animated } from 'react-spring';
import defaultStyles from './styles/Drawer.module.scss.js';
import { useDrawer } from './utils/useDrawer.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { useScrollLock } from '../../hooks/useScrollLock.js';
import { ButtonGroup } from '../button/ButtonGroup.js';
import { Button } from '../button/Button.js';
import { Portal } from '../portal/Portal.js';

const Drawer = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    theme,
    headerStyle,
    bodyStyle,
    title,
    okText,
    cancelText,
    header = null,
    footer = null,
    width = 250,
    height = 250,
    placement = 'right',
    mask = true,
    visible,
    closable = true,
    maskClosable = true,
    confirmLoading,
    escToExit = true,
    autoFocus,
    okButtonProps,
    cancelButtonProps,
    closeIcon,
    onCancel,
    component,
    children,
    lockScroll = true,
    unmountOnExit = true
  } = props;
  const styles = composeThemeFromProps(defaultStyles, {
    theme
  }, {
    compose: Compose.Merge
  });
  const Component = component || 'div';
  const {
    contentWrapperRef,
    springConfig,
    overlayStyle,
    drawerWrapperRef,
    inExit
  } = useDrawer({
    escToExit,
    width,
    height,
    visible,
    placement,
    onCancel
  });
  useImperativeHandle(ref, () => drawerWrapperRef.current);
  useScrollLock(lockScroll && visible);
  const element = /*#__PURE__*/jsxs(Fragment, {
    children: [header !== null && header ? /*#__PURE__*/jsx("div", {
      className: styles.header,
      style: headerStyle,
      children: header
    }) : /*#__PURE__*/jsxs("div", {
      style: headerStyle,
      className: styles.header,
      children: [title && /*#__PURE__*/jsx("div", {
        className: styles.title,
        children: title
      }), closable && /*#__PURE__*/jsx(Button, {
        theme: {
          root: styles.close
        },
        onClick: onCancel,
        status: "secondary",
        shape: "circle",
        variant: "ghost",
        flat: true,
        noHover: true,
        size: "xs",
        children: closeIcon || /*#__PURE__*/jsx(X, {})
      })]
    }), /*#__PURE__*/jsx("div", {
      ref: contentWrapperRef,
      style: bodyStyle,
      className: clsx(styles.content, {
        [styles.nofooter]: footer === null,
        [styles.noheader]: title === null
      }),
      children: children
    }), footer !== null && (footer ? /*#__PURE__*/jsx("div", {
      className: styles.footer,
      children: footer
    }) : /*#__PURE__*/jsx("div", {
      className: styles.footer,
      children: /*#__PURE__*/jsxs(ButtonGroup, {
        children: [/*#__PURE__*/jsx(Button, {
          status: "secondary",
          onClick: e => onCancel(e),
          ...cancelButtonProps,
          children: cancelText || 'Cancel'
        }), /*#__PURE__*/jsx(Button, {
          status: "primary",
          loading: confirmLoading,
          onClick: props.onOk,
          ...okButtonProps,
          children: okText || 'Ok'
        })]
      })
    }))]
  });

  // Only enable FocusLock when drawer is fully opened, to avoid element shaking.
  const dom = autoFocus ? /*#__PURE__*/jsx(FocusLock, {
    as: "span",
    disabled: !visible,
    autoFocus: autoFocus,
    children: element
  }) : element;
  return /*#__PURE__*/jsx(Portal, {
    children: /*#__PURE__*/jsx(Transition, {
      native: true,
      items: visible,
      from: springConfig.from,
      enter: () => {
        return springConfig.to;
      },
      leave: () => {
        return springConfig.from;
      },
      children: (transitionStyles, item) => {
        const shouldRenderComponent = item || !unmountOnExit;
        return shouldRenderComponent && /*#__PURE__*/jsxs(Component, {
          hidden: !item && !unmountOnExit,
          ref: drawerWrapperRef,
          className: clsx(styles.wrapper, {
            [styles.mask]: !mask
          }),
          style: {
            position: 'fixed'
          },
          children: [mask ? /*#__PURE__*/jsx(animated.div, {
            style: overlayStyle,
            className: styles.mask,
            onClick: e => {
              if (!inExit.current && maskClosable && props.onCancel) {
                onCancel(e);
              }
            }
          }) : null, /*#__PURE__*/jsx(animated.div, {
            style: Object.assign(placement === 'left' || placement === 'right' ? {
              width
            } : {
              height
            }, placement && {
              [placement]: 0
            }, transitionStyles),
            className: styles.root,
            children: /*#__PURE__*/jsx("div", {
              className: styles.inner,
              children: dom
            })
          })]
        });
      }
    })
  });
});
Drawer.displayName = 'Drawer';

export { Drawer };
